import { useEffect, useRef, useState } from 'react';
import { CollapsiblePanel } from '../UI/CollapsiblePanel';
import { Select } from '../UI/Select';
import { Slider } from '../UI/Slider';
import { Toggle } from '../UI/Toggle';
import { useWakeLock } from '../../hooks/useWakeLock';

interface BurnInFixTestProps {
  settings: Record<string, any>;
  onSettingsChange: (settings: Record<string, any>) => void;
}

export function BurnInFixTest({ settings, onSettingsChange }: BurnInFixTestProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const [showInfo, setShowInfo] = useState(false);
  const [acknowledged, setAcknowledged] = useState(() => {
    try { return sessionStorage.getItem('burnInWarningAck') === '1'; } catch { return false; }
  });
  const { supported: wakeLockSupported, acquired: wakeLockAcquired } = useWakeLock(acknowledged);

  const acknowledge = () => {
    try { sessionStorage.setItem('burnInWarningAck', '1'); } catch { /* ignore */ }
    setAcknowledged(true);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Do not run the flickering animation until the photosensitivity warning is acknowledged.
    if (!acknowledged) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const pattern = settings.pattern || 'scrolling-bars';
    const speed = settings.speed || 50;
    const colorCycle = settings.colorCycle !== false;
    const shiftAmount = settings.shiftAmount || 5;

    let offset = 0;
    let hue = 0;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update offset based on speed
      offset += speed / 10;
      if (colorCycle) {
        hue = (hue + 0.5) % 360;
      }

      const baseColor = colorCycle 
        ? `hsl(${hue}, 100%, 50%)`
        : '#ffffff';

      switch (pattern) {
        case 'scrolling-bars':
          drawScrollingBars(ctx, canvas.width, canvas.height, offset, baseColor);
          break;
        case 'pixel-shift':
          drawPixelShift(ctx, canvas.width, canvas.height, offset, shiftAmount, baseColor);
          break;
        case 'wave-pattern':
          drawWavePattern(ctx, canvas.width, canvas.height, offset, baseColor);
          break;
        case 'spiral':
          drawSpiral(ctx, canvas.width, canvas.height, offset, baseColor);
          break;
        case 'plasma':
          drawPlasma(ctx, canvas.width, canvas.height, offset, colorCycle);
          break;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [settings, acknowledged]);

  return (
    <div className="relative w-full h-full bg-black">
      <canvas ref={canvasRef} className="absolute inset-0" />

      {/* Photosensitivity warning — must be acknowledged before the animation runs */}
      {!acknowledged && (
        <div className="absolute inset-0 z-[60] flex items-center justify-center p-6 bg-black/85 backdrop-blur-md animate-fade-in">
          <div
            className="max-w-md w-full rounded-2xl p-6 text-center animate-modal-enter"
            style={{
              background: 'rgba(14, 14, 22, 0.95)',
              border: '1px solid rgba(255, 170, 0, 0.3)',
              boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
            }}
          >
            <div className="text-4xl mb-3">⚠️</div>
            <h2 className="text-lg font-bold text-yellow-400 mb-2">Photosensitivity Warning</h2>
            <p className="text-sm text-white/60 leading-relaxed mb-1">
              This tool displays <span className="text-white/80 font-medium">rapidly flashing colors, flickering, and moving patterns</span>.
            </p>
            <p className="text-sm text-white/60 leading-relaxed mb-5">
              These effects may trigger seizures or discomfort in individuals with photosensitive epilepsy or sensitivity to light and motion. Viewer discretion is advised.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={acknowledge}
                className="px-5 py-2.5 rounded-xl font-semibold text-black transition-all duration-200"
                style={{ background: '#ffaa00' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#ffbb33')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#ffaa00')}
              >
                I understand — start
              </button>
            </div>
            <p className="text-[10px] text-white/25 mt-4">
              Shown once per session. Select another test from the sidebar to exit.
            </p>
          </div>
        </div>
      )}

      <CollapsiblePanel title="Burn-In Prevention">
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-sm font-medium mb-2">Pattern Type</label>
            <Select
              value={settings.pattern || 'scrolling-bars'}
              onChange={(value) => onSettingsChange({ ...settings, pattern: value })}
              options={[
                { value: 'scrolling-bars', label: 'Scrolling Bars' },
                { value: 'pixel-shift', label: 'Pixel Shift' },
                { value: 'wave-pattern', label: 'Wave Pattern' },
                { value: 'spiral', label: 'Spiral' },
                { value: 'plasma', label: 'Plasma Effect' },
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Speed: {settings.speed || 50}
            </label>
            <Slider
              value={settings.speed || 50}
              onChange={(value) => onSettingsChange({ ...settings, speed: value })}
              min={10}
              max={100}
            />
          </div>

          {settings.pattern === 'pixel-shift' && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Shift Amount: {settings.shiftAmount || 5}px
              </label>
              <Slider
                value={settings.shiftAmount || 5}
                onChange={(value) => onSettingsChange({ ...settings, shiftAmount: value })}
                min={1}
                max={20}
              />
            </div>
          )}

          <div>
            <Toggle
              label="Color Cycling"
              checked={settings.colorCycle !== false}
              onChange={(checked) => onSettingsChange({ ...settings, colorCycle: checked })}
            />
          </div>

          {/* Wake lock status — compact single line when all is well */}
          {wakeLockAcquired ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] text-[#00ff88]"
              style={{ background: 'rgba(0,255,136,0.07)', border: '1px solid rgba(0,255,136,0.18)' }}>
              <span className="text-[8px]">●</span> Screen stay-awake active
            </div>
          ) : wakeLockSupported ? (
            <div className="px-2.5 py-1.5 rounded-lg text-[11px] text-yellow-400/70"
              style={{ background: 'rgba(255,170,0,0.06)', border: '1px solid rgba(255,170,0,0.15)' }}>
              Acquiring screen stay-awake…
            </div>
          ) : (
            <div className="px-2.5 py-2 rounded-lg text-[11px]"
              style={{ background: 'rgba(255,170,0,0.07)', border: '1px solid rgba(255,170,0,0.2)' }}>
              <p className="font-semibold text-yellow-400">⚠ Disable screen saver &amp; sleep</p>
              <p className="text-yellow-300/60 mt-0.5">Your browser can't keep the screen awake. Set your OS sleep/screensaver to "Never" so this can run continuously.</p>
            </div>
          )}

          {/* Collapsible info — keeps the panel compact by default */}
          <div>
            <button
              onClick={() => setShowInfo((v) => !v)}
              className="w-full flex items-center justify-between text-[11px] text-white/40 hover:text-white/70 transition-colors py-1"
            >
              <span>How it works</span>
              <svg xmlns="http://www.w3.org/2000/svg"
                className={`h-3 w-3 transition-transform duration-200 ${showInfo ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showInfo && (
              <p className="text-[11px] text-white/40 leading-relaxed mt-1 animate-fade-in">
                Moving patterns keep every pixel changing so none stays lit at constant brightness. Run 30–90 min to exercise OLED subpixels evenly and reduce image retention. Works on LCD/LED too.
              </p>
            )}
          </div>
        </div>
      </CollapsiblePanel>
    </div>
  );
}

// Pattern drawing functions
function drawScrollingBars(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  offset: number,
  color: string
) {
  const barWidth = 50;
  const spacing = 100;
  
  ctx.fillStyle = color;
  
  for (let x = -spacing; x < width + spacing; x += spacing) {
    const xPos = (x + offset) % (width + spacing);
    ctx.fillRect(xPos, 0, barWidth, height);
  }
}

function drawPixelShift(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  offset: number,
  shiftAmount: number,
  color: string
) {
  const gridSize = 20;
  const shiftX = Math.sin(offset / 50) * shiftAmount;
  const shiftY = Math.cos(offset / 50) * shiftAmount;
  
  ctx.fillStyle = color;
  
  for (let x = 0; x < width; x += gridSize * 2) {
    for (let y = 0; y < height; y += gridSize * 2) {
      ctx.fillRect(x + shiftX, y + shiftY, gridSize, gridSize);
    }
  }
}

function drawWavePattern(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  offset: number,
  color: string
) {
  ctx.fillStyle = color;
  
  const waveHeight = height / 8;
  const frequency = 0.015;
  const numWaves = 8;
  const waveSpacing = height / numWaves;
  
  // Draw multiple overlapping horizontal waves
  for (let wave = 0; wave < numWaves; wave++) {
    const baseY = (wave + 0.5) * waveSpacing;
    const phaseShift = wave * Math.PI / 4;
    
    ctx.beginPath();
    for (let x = 0; x <= width; x += 2) {
      const y = baseY + Math.sin((x + offset + phaseShift * 100) * frequency) * waveHeight;
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineWidth = 3;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
  
  // Draw vertical waves
  for (let wave = 0; wave < numWaves; wave++) {
    const baseX = (wave + 0.5) * (width / numWaves);
    const phaseShift = wave * Math.PI / 4;
    
    ctx.beginPath();
    for (let y = 0; y <= height; y += 2) {
      const x = baseX + Math.cos((y + offset + phaseShift * 100) * frequency) * (width / 16);
      if (y === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.lineWidth = 3;
    ctx.strokeStyle = color;
    ctx.stroke();
  }
}

function drawSpiral(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  offset: number,
  color: string
) {
  const centerX = width / 2;
  const centerY = height / 2;
  const maxRadius = Math.sqrt(width * width + height * height) / 2;
  const totalTurns = 20;
  const totalAngle = Math.PI * 2 * totalTurns;
  
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.beginPath();
  
  for (let angle = 0; angle < totalAngle; angle += 0.08) {
    const radius = (angle / totalAngle) * maxRadius;
    const x = centerX + Math.cos(angle + offset / 50) * radius;
    const y = centerY + Math.sin(angle + offset / 50) * radius;
    
    if (angle === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  
  ctx.stroke();
}

function drawPlasma(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  offset: number,
  colorCycle: boolean
) {
  const imageData = ctx.createImageData(width, height);
  const data = imageData.data;
  
  const time = offset / 50;
  
  for (let y = 0; y < height; y += 2) {
    for (let x = 0; x < width; x += 2) {
      const value = Math.sin(x / 16 + time) +
                   Math.sin(y / 8 + time) +
                   Math.sin((x + y) / 16 + time) +
                   Math.sin(Math.sqrt(x * x + y * y) / 8 + time);
      
      const index = (y * width + x) * 4;
      
      if (colorCycle) {
        const hue = (value + 4) / 8 * 360;
        const rgb = hslToRgb(hue, 100, 50);
        data[index] = rgb[0];
        data[index + 1] = rgb[1];
        data[index + 2] = rgb[2];
      } else {
        const brightness = ((value + 4) / 8) * 255;
        data[index] = brightness;
        data[index + 1] = brightness;
        data[index + 2] = brightness;
      }
      data[index + 3] = 255;
      
      // Fill 2x2 block for performance
      if (x + 1 < width) {
        data[index + 4] = data[index];
        data[index + 5] = data[index + 1];
        data[index + 6] = data[index + 2];
        data[index + 7] = 255;
      }
      if (y + 1 < height) {
        const nextRow = ((y + 1) * width + x) * 4;
        data[nextRow] = data[index];
        data[nextRow + 1] = data[index + 1];
        data[nextRow + 2] = data[index + 2];
        data[nextRow + 3] = 255;
        if (x + 1 < width) {
          data[nextRow + 4] = data[index];
          data[nextRow + 5] = data[index + 1];
          data[nextRow + 6] = data[index + 2];
          data[nextRow + 7] = 255;
        }
      }
    }
  }
  
  ctx.putImageData(imageData, 0, 0);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100;
  l /= 100;
  
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  
  let r = 0, g = 0, b = 0;
  
  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0;
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x;
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c;
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c;
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x;
  }
  
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255)
  ];
}
